/* PAGINA 3 · LISTA TRADEBOT
 *
 * È la schermata che si apre quando qualcosa non va, spesso dal telefono e
 * fuori casa. Quindi: la stessa riga in due forme (scheda sotto i 720px,
 * tabella sopra) rese ENTRAMBE, con il CSS a scegliere; i tre numeri restano
 * tre a ogni larghezza; e i due flussi — clona e modifica dal vivo — stanno su
 * ogni riga, non dentro un menu che va cercato.
 *
 * Cosa NON c'è, e perché: Tenuta e Nei ribassi. Sono metriche di backtest
 * (`walkForwardScore`, `bearYearsAvg`) e la entity Tradebot non ha un
 * riferimento al backtest da cui viene. Mostrarle qui vorrebbe dire inventarle.
 * La mancanza è dichiarata a schermo e annotata in RICHIESTE-API.md.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avvisi,
  ContenitoreDati,
  Distintivo,
  IntestazionePagina,
  Pulsante,
  TernaMetriche,
  usaAvvisi,
} from '../../componenti';
import { tradebots, usaRichiesta } from '../../api';
import type { StatoDati, Tradebot } from '../../api';
import { drawdown as fmtDrawdown, num, pct } from '../../lib/formato';
import { tonoRendimento } from '../../lib/metriche';
import { capitaleDi, ordinaPerImportanza, vitalitaDi } from '../../lib/vitalita';
import {
  CAMPI_RIGA_BOT,
  coppiaBot,
  modalitaBot,
  periodoBot,
  sedeBot,
  statoBot,
  ternaBot,
} from './modello';
import {
  DistintiviModalita,
  EtichettaStato,
  IconaClona,
  IconaConPallino,
  IconaModifica,
} from './Comuni';
import {
  PAGINE_LISTA,
  RIGHE_PER_PAGINA,
  usaCorrelazioni,
  type Correlazione,
} from './correlazione';
import { IndicatoriFitti } from './Indicatori';
import { FoglioClona } from './FoglioClona';
import { FoglioModifica } from './FoglioModifica';
import './tradebot.css';

/** Due tratti: una dipendenza esterna per una croce e' una dipendenza che si
 *  aggiorna e rompe. Vale la stessa regola delle icone di `Base.tsx`. */
function IconaPiu({ dim = 14 }: { dim?: number }) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

type Filtro = 'tutti' | 'reali' | 'simulati' | 'catena';

const FILTRI: { chiave: Filtro; etichetta: string }[] = [
  { chiave: 'tutti', etichetta: 'Tutti' },
  { chiave: 'reali', etichetta: 'Denaro vero' },
  { chiave: 'simulati', etichetta: 'Simulati' },
  { chiave: 'catena', etichetta: 'Su catena' },
];

function passa(bot: Tradebot, filtro: Filtro): boolean {
  if (filtro === 'tutti') return true;
  if (filtro === 'reali') return bot.useRealMoney;
  if (filtro === 'simulati') return !bot.useRealMoney;
  return bot.walletId != null;
}

export function ListaTradebot() {
  const naviga = useNavigate();
  const { avvisi, mostra } = usaAvvisi();

  const [filtro, setFiltro] = useState<Filtro>('tutti');
  const [botDaClonare, setBotDaClonare] = useState<Tradebot | null>(null);
  const [botDaModificare, setBotDaModificare] = useState<Tradebot | null>(null);

  const richiesta = usaRichiesta(() => tradebots.elenco(CAMPI_RIGA_BOT), []);

  const tutti = useMemo(() => richiesta.dati?.data ?? [], [richiesta.dati]);
  /* L'ordine è per importanza, non per id decrescente: l'id decrescente è la
     data di creazione, e con undici bot fermi e uno in marcia metteva in cima
     l'ultimo creato — che era anche l'unico vivo, ma per coincidenza. Chi sta
     muovendo denaro adesso va guardato per primo; chi è stato spento il mese
     scorso non ha niente da dire. */
  const visibili = useMemo(
    () => ordinaPerImportanza(tutti.filter((b) => passa(b, filtro))),
    [tutti, filtro],
  );

  /* La stessa spiegazione ripetuta dodici volte smette di essere una
     spiegazione. Quando tutti i bot visibili hanno lo stesso motivo per cui il
     drawdown manca — ed è il caso normale, perché il motore non lo registra per
     nessuno — si dice una volta sopra l'elenco e sparisce dalle righe. */
  const motivoDrawdownComune = useMemo(() => {
    if (visibili.length < 2) return null;
    const motivi = visibili.map((b) => ternaBot(b).motivoDrawdownMancante);
    const primo = motivi[0];
    /* Solo se vale per TUTTI: basta un bot con un drawdown vero, o con un motivo
       diverso, e la riga generale mentirebbe su quel bot. */
    return primo != null && motivi.every((m) => m === primo) ? primo : null;
  }, [visibili]);

  const conteggi = useMemo(() => {
    const c: Record<Filtro, number> = { tutti: 0, reali: 0, simulati: 0, catena: 0 };
    for (const f of FILTRI) c[f.chiave] = tutti.filter((b) => passa(b, f.chiave)).length;
    return c;
  }, [tutti]);

  /* Quanto è costata davvero questa lista: `trades` è una relazione EAGER e
     `fields` non la tocca. Il numero non è un vezzo — è la misura del problema
     descritto in RICHIESTE-API.md §2, e sul telefono si sente. */
  const operazioniScaricate = useMemo(
    () => tutti.reduce((somma, b) => somma + (b.trades?.length ?? 0), 0),
    [tutti],
  );

  const conDenaroVero = conteggi.reali;

  /* Tenuta e Nei ribassi vengono dal backtest con gli stessi identici
     parametri: il legame si riconosce dalla configurazione, non da un id di
     provenienza che i bot esistenti non hanno mai avuto. È una richiesta a
     parte perché non deve ritardare la lista: i bot compaiono subito, le due
     soglie si riempiono quando arrivano. */
  const correlazioni = usaCorrelazioni(tutti, PAGINE_LISTA);

  function apriDettaglio(id: number) {
    naviga(`/tradebot/${id}`);
  }

  return (
    <div className="sb-cornice">
      <IntestazionePagina
        titolo="Tradebot"
        sotto={
          <span className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
            <span className="sb-nota">
              {tutti.length === 0
                ? 'nessun bot'
                : `${num(tutti.length)} bot · ${num(tutti.filter((b) => b.isRunning).length)} in esecuzione`}
            </span>
            {conDenaroVero > 0 && (
              <Distintivo tono="cautela">
                {conDenaroVero === 1 ? '1 con denaro vero' : `${num(conDenaroVero)} con denaro vero`}
              </Distintivo>
            )}
          </span>
        }
        azioni={
          /* Il modulo di creazione ha un ingresso suo, evidente: prima si
             arrivava qui solo dal dettaglio di un backtest, e chi voleva un bot
             da zero non aveva nessun posto da cui partire. */
          <Pulsante variante="primario" onClick={() => naviga('/tradebot/nuovo')}>
            <IconaPiu />
            Nuovo tradebot
          </Pulsante>
        }
      />

      <div style={{ padding: 'var(--sb-pad-pagina)', borderBottom: '1px solid var(--sb-bordo-tenue)' }}>
        {/* Non è un `tablist`: non ci sono pannelli, c'è una lista sola che si
            restringe. Un gruppo di pulsanti a pressione dice la cosa giusta. */}
        <div className="tb-filtri" role="group" aria-label="Filtra i bot">
          {FILTRI.map((f) => (
            <button
              key={f.chiave}
              type="button"
              aria-pressed={filtro === f.chiave}
              className={`tb-filtro${filtro === f.chiave ? ' tb-filtro--attivo' : ''}`}
              onClick={() => setFiltro(f.chiave)}
            >
              {f.etichetta}
              <span className="tb-filtro__conteggio">{conteggi[f.chiave]}</span>
            </button>
          ))}
        </div>

        {operazioniScaricate > 0 && (
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
            La risposta ha portato {num(operazioniScaricate)} operazioni di tutti i bot: la
            relazione <code>trades</code> è eager e <code>fields</code> non la esclude. Vedi
            RICHIESTE-API.md §2.
          </div>
        )}

        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
          Tenuta e Nei ribassi arrivano dal backtest con gli stessi parametri, riconosciuto dalla
          configurazione e non da un id: token avvolti e stablecoin sono equiparati, altrimenti un
          bot che compra BTCB su BSC non si riconoscerebbe nel suo backtest su BTC. Qui la ricerca
          si ferma alle prime {num(PAGINE_LISTA * RIGHE_PER_PAGINA)} righe per taglio; il dettaglio del bot
          scava più a fondo.
        </div>

        {/* Detta una volta, non sotto ogni riga: era la stessa frase per dodici
            bot, e una spiegazione ripetuta dodici volte non viene più letta. */}
        {motivoDrawdownComune && (
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
            Il drawdown non è una colonna del motore dei tradebot: si ricostruisce dai saldi di
            uscita delle operazioni chiuse. Per tutti i bot in elenco resta n/d —{' '}
            {motivoDrawdownComune}.
          </div>
        )}
      </div>

      <ContenitoreDati
        stato={richiesta.stato === 'pieno' && visibili.length === 0 ? 'vuoto' : richiesta.stato}
        causaVuoto={
          tutti.length === 0
            ? 'Non c’è ancora nessun tradebot. Il modo consigliato è partire da un backtest — dal suo dettaglio, col pulsante «Crea tradebot» — così i parametri arrivano già misurati. Da qui si può anche partire da zero, ma nessuna misura sosterrebbe i valori.'
            : `Il filtro “${FILTRI.find((f) => f.chiave === filtro)?.etichetta}” non lascia passare nessuno dei ${num(tutti.length)} bot.`
        }
        azioneVuoto={
          tutti.length > 0 && filtro !== 'tutti'
            ? { etichetta: 'Mostra tutti i bot', onClick: () => setFiltro('tutti') }
            : tutti.length === 0
              ? { etichetta: 'Scegli un backtest', onClick: () => naviga('/backtest') }
              : undefined
        }
        messaggioErrore={richiesta.errore?.message}
        onRiprova={richiesta.ricarica}
      >
        {/* ---------- telefono: schede ---------- */}
        <div className="tb-schede">
          {visibili.map((bot) => (
            <SchedaBot
              key={bot.id}
              bot={bot}
              correlazione={correlazioni.dati?.get(bot.id)}
              statoCorrelazione={correlazioni.stato}
              motivoDrawdownDetto={motivoDrawdownComune != null}
              onApri={() => apriDettaglio(bot.id)}
              onClona={() => setBotDaClonare(bot)}
              onModifica={() => setBotDaModificare(bot)}
            />
          ))}
        </div>

        {/* ---------- tablet e desktop: tabella ---------- */}
        <div className="tb-tabella-guscio">
          <div className="sb-scorri-x">
            <table className="sb-tabella">
              <thead>
                <tr>
                  <th>Bot</th>
                  <th>Stato</th>
                  {/* Il capitale governato: era l'unica cosa che questa lista
                      non diceva, e il motivo per cui si apre. */}
                  <th>Capitale</th>
                  <th className="sb-col-profitto">Profitto</th>
                  <th className="sb-col-drawdown">Drawdown</th>
                  <th>Ops</th>
                  <th className="tb-cella-soglie">Tenuta · Nei ribassi</th>
                  <th>Periodo</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {visibili.map((bot) => (
                  <RigaBot
                    key={bot.id}
                    bot={bot}
                    correlazione={correlazioni.dati?.get(bot.id)}
                    statoCorrelazione={correlazioni.stato}
                    motivoDrawdownDetto={motivoDrawdownComune != null}
                    onApri={() => apriDettaglio(bot.id)}
                    onClona={() => setBotDaClonare(bot)}
                    onModifica={() => setBotDaModificare(bot)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ContenitoreDati>

      <FoglioClona
        bot={botDaClonare}
        aperta={botDaClonare != null}
        onChiudi={() => setBotDaClonare(null)}
        onCreati={(quanti) => {
          richiesta.ricarica();
          mostra(quanti === 1 ? '1 bot creato' : `${quanti} bot creati`);
        }}
      />

      <FoglioModifica
        bot={botDaModificare}
        aperta={botDaModificare != null}
        onChiudi={() => setBotDaModificare(null)}
        onApplicato={(quante) => {
          setBotDaModificare(null);
          richiesta.ricarica();
          mostra(
            quante === 1
              ? '1 modifica salvata · attiva dalla prossima candela'
              : `${quante} modifiche salvate · attive dalla prossima candela`,
          );
        }}
      />

      <Avvisi avvisi={avvisi} />
    </div>
  );
}

/* ==========================================================================
 * Scheda (telefono)
 * ======================================================================== */

/**
 * Il capitale che il bot governa, in una riga.
 *
 * Era `n/d` su ogni bot: la somma teneva conto della sola valuta, e un bot con
 * tutto in BTCB risultava «0,00 · parziale» — esatto e inutile. `capitaleDi`
 * valuta l'asset al prezzo più recente che si conosce e dichiara da dove viene
 * quel prezzo, perché cambia quanto ci si può fidare del numero.
 */
function Capitale({ bot, compatto = false }: { bot: Tradebot; compatto?: boolean }) {
  const cap = capitaleDi(bot);
  const cifra = cap.valore == null ? 'n/d' : `${num(cap.valore, compatto ? 0 : 2)} ${bot.currency}`;

  if (compatto) {
    return (
      <>
        <span className="sb-numero">{cifra}</span>
        {cap.motivo && <div className="sb-tabella__sotto">{cap.motivo}</div>}
      </>
    );
  }

  /* Stili in linea e non una classe nuova: il foglio `tradebot.css` è in mano a
     un altro lavoro in corso, e una classe dichiarata altrove sarebbe una
     regola orfana. Sono tre proprietà, tutte da token. */
  return (
    <div className="sb-riga" style={{ gap: 'var(--sb-s-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
      <span
        className="sb-numero"
        style={{ fontSize: 'var(--sb-t-16)', fontWeight: 'var(--sb-peso-forte)' }}
      >
        {cifra}
      </span>
      <span className="sb-nota">capitale governato</span>
      {cap.valore != null && cap.parziale && <Distintivo tono="cautela">parziale</Distintivo>}
      {cap.motivo && <div className="sb-nota" style={{ width: '100%' }}>{cap.motivo}</div>}
    </div>
  );
}

function SchedaBot({
  bot,
  correlazione,
  statoCorrelazione,
  motivoDrawdownDetto,
  onApri,
  onClona,
  onModifica,
}: {
  bot: Tradebot;
  correlazione: Correlazione | undefined;
  statoCorrelazione: StatoDati;
  /** Vero quando il motivo del drawdown mancante è già scritto sopra l'elenco:
   *  ripeterlo sotto ogni scheda costa due righe per bot e non aggiunge niente. */
  motivoDrawdownDetto: boolean;
  onApri: () => void;
  onClona: () => void;
  onModifica: () => void;
}) {
  const stato = statoBot(bot);
  const modalita = modalitaBot(bot);
  const terna = ternaBot(bot);
  const p = periodoBot(bot);
  /* `isRunning` dice che il bot è avviato nel registro; il battito dice se il
     processo è ancora vivo. Sono due domande diverse, e per un bot con denaro
     vero la seconda è quella che conta. */
  const v = vitalitaDi(bot);

  /* «Non respira» prende il bordo d'allarme come «errore»: un bot con denaro vero il cui
     processo ha smesso di dare segno di sé non è meno grave di uno che ha registrato un errore
     — è più grave, perché non ha registrato niente. Senza questo, in lista lo stato nuovo si
     vedeva solo nell'etichetta e la scheda restava indistinguibile dalle altre. */
  const allarmante = stato.chiave === 'errore' || stato.chiave === 'non-respira';
  const classi = [
    'tb-scheda',
    allarmante ? 'tb-scheda--errore' : modalita.denaroReale ? 'tb-scheda--reale' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classi}>
      <button type="button" className="tb-apri" onClick={onApri}>
        <div className="tb-testata">
          <IconaConPallino strategia={bot.strategyName} stato={stato} dim={26} />
          <div className="tb-testata__corpo">
            <div className="tb-testata__titolo">
              {/* Anche su telefono l'identificativo viene per primo: e' il nome con cui il bot
                  compare nei messaggi Telegram, che si leggono proprio da qui. */}
              <span className="tb-id">#{bot.id}</span>
              <span className="tb-coppia">{coppiaBot(bot)}</span>
              <DistintiviModalita bot={bot} />
            </div>
            <div className="tb-sotto">
              {bot.strategyName} · {sedeBot(bot)}
            </div>
          </div>
        </div>

        <div className="tb-stato-riga" style={{ marginTop: 'var(--sb-s-5)' }}>
          <EtichettaStato stato={stato} />
          {stato.nota && <div className="tb-stato-nota">{stato.nota}</div>}
          {/* Il battito si scrive solo se lo stato non lo dice gia': «Non respira» porta con se'
              la sua nota, e ripeterla sotto sarebbe la stessa frase due volte. */}
          {!v.allarme && v.stato !== 'fermo' && <div className="tb-stato-nota">{v.testo}</div>}
        </div>

        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Capitale bot={bot} />
        </div>

        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <TernaMetriche
            profitto={terna.profitto}
            drawdown={terna.drawdown}
            operazioni={terna.operazioni}
            periodo={p.etichetta}
            durata={p.durata}
            densita="compatta"
          />
          {/* `TernaBotVista` scrive sempre il motivo del drawdown mancante: su
              dodici schede sono ventiquattro righe della stessa frase. Qui il
              motivo compare solo quando NON è già detto sopra l'elenco. */}
          {!motivoDrawdownDetto && terna.motivoDrawdownMancante && (
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
              Drawdown non calcolabile: {terna.motivoDrawdownMancante}.
            </div>
          )}
        </div>

        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <IndicatoriFitti correlazione={correlazione} stato={statoCorrelazione} />
        </div>
      </button>

      <div className="tb-azioni-riga">
        <Pulsante onClick={onModifica}>
          <IconaModifica />
          Modifica
        </Pulsante>
        <Pulsante variante="tenue" onClick={onClona}>
          <IconaClona />
          Clona
        </Pulsante>
      </div>
    </div>
  );
}

/* ==========================================================================
 * Riga di tabella (tablet e desktop)
 * ======================================================================== */

function RigaBot({
  bot,
  correlazione,
  statoCorrelazione,
  motivoDrawdownDetto,
  onApri,
  onClona,
  onModifica,
}: {
  bot: Tradebot;
  correlazione: Correlazione | undefined;
  statoCorrelazione: StatoDati;
  motivoDrawdownDetto: boolean;
  onApri: () => void;
  onClona: () => void;
  onModifica: () => void;
}) {
  const stato = statoBot(bot);
  const terna = ternaBot(bot);
  const p = periodoBot(bot);
  const v = vitalitaDi(bot);

  return (
    <tr onClick={onApri} style={{ cursor: 'pointer' }}>
      <td className="sb-cella-testo">
        <span className="sb-tabella__strategia">
          <IconaConPallino strategia={bot.strategyName} stato={stato} dim={26} />
          <span>
            <span className="tb-testata__titolo">
              {/* L'identificativo per primo: e' come il bot viene chiamato nei log del server,
                  nei messaggi Telegram e quando se ne parla. Senza, per collegare una riga a un
                  messaggio ricevuto sul telefono bisogna aprirla. */}
              <span className="tb-id">#{bot.id}</span>
              <span>{coppiaBot(bot)}</span>
              <DistintiviModalita bot={bot} />
            </span>
            <span className="sb-tabella__sotto">
              {bot.strategyName} · {sedeBot(bot)}
            </span>
          </span>
        </span>
      </td>

      <td style={{ textAlign: 'left', fontFamily: 'var(--sb-font-testo)' }}>
        <EtichettaStato stato={stato} />
        {stato.nota && <div className="sb-tabella__sotto">{stato.nota}</div>}
        {!v.allarme && v.stato !== 'fermo' && <div className="sb-tabella__sotto">{v.testo}</div>}
      </td>

      <td className="sb-tono-neutro">
        <Capitale bot={bot} compatto />
      </td>

      <td className={`sb-tono-${tonoRendimento(terna.profitto)}`}>
        {terna.profitto == null ? 'n/d' : pct(terna.profitto, 2)}
      </td>

      <td className="sb-tono-perdita">
        {terna.drawdown == null ? 'n/d' : fmtDrawdown(terna.drawdown)}
        {!motivoDrawdownDetto && terna.motivoDrawdownMancante && (
          <div className="sb-tabella__sotto">{terna.motivoDrawdownMancante}</div>
        )}
      </td>

      <td className="sb-tono-neutro">{terna.operazioni == null ? 'n/d' : num(terna.operazioni)}</td>

      <td className="tb-cella-soglie">
        <IndicatoriFitti correlazione={correlazione} stato={statoCorrelazione} />
      </td>

      <td className="sb-tono-neutro" style={{ fontWeight: 'var(--sb-peso-medio)', color: 'var(--sb-testo-tenue)' }}>
        {p.etichetta}
        {p.durata && <div className="sb-tabella__sotto">{p.durata}</div>}
      </td>

      <td onClick={(e) => e.stopPropagation()}>
        <span style={{ display: 'inline-flex', gap: 'var(--sb-s-2)' }}>
          <Pulsante variante="icona" onClick={onModifica} title="Modifica dal vivo" aria-label={`Modifica dal vivo ${coppiaBot(bot)}`}>
            <IconaModifica dim={15} />
          </Pulsante>
          <Pulsante variante="icona" onClick={onClona} title="Clona su altri conti" aria-label={`Clona ${coppiaBot(bot)} su altri conti`}>
            <IconaClona dim={15} />
          </Pulsante>
        </span>
      </td>
    </tr>
  );
}
